import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, ScrollToTop, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const AboutPage = () => {
  const { t } = useLanguage();
  
  return (
    <main className="pt-32" data-testid="about-page">
      <SEOHead
        title="À Propos | Body-Up Personal Training Nyon | Laurent Waelti Coach Sportif"
        description="Découvrez Body-Up Personal Training Nyon. Laurent Waelti, coach sportif diplômé depuis 2005, et son équipe vous accueillent dans un studio privé 150m². 20 ans d'expertise."
        path="/about"
      />
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">🏢 {t.about.subtitle}</span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-4">{t.about.title}</h1>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
                {t.about.description}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={STUDIO_IMAGE} 
                  alt="Studio Body-Up Personal Training Nyon - Salle de sport privée 150m²" 
                  className="rounded-2xl w-full"
                  width="600"
                  height="400"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute bottom-4 right-4 bg-primary text-white p-5 rounded-2xl shadow-xl">
                  <div className="text-4xl font-bold">14+</div>
                  <div className="text-sm">{t.about.yearsExcellence}</div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">{t.about.studioTitle}</h2>
                <div className="space-y-4 text-gray-300">
                  <p>{t.about.studioText1}</p>
                  <p>{t.about.studioText2}</p>
                  <p>{t.about.studioText3}</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">👥 {t.about.teamSubtitle}</span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mt-4">{t.about.teamTitle}</h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Laurent */}
            <AnimatedSection>
              <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-primary/30 transition-all hover:shadow-xl hover:shadow-primary/10 group">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={LAURENT_IMAGE} 
                    alt="Laurent Waelti - Fondateur" 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <span className="text-primary text-sm font-semibold">🏆 Fondateur & Personal Trainer</span>
                  <h3 className="text-2xl font-bold text-white mt-2 mb-4">Laurent Waelti</h3>
                  <p className="text-gray-400 mb-6">
                    Personal trainer depuis 2005 et fondateur de Body-Up en 2011, Laurent cumule plus de 20 ans d'expertise dans le domaine. 
                    Sportif accompli (boxe, jiu-jitsu brésilien, fitness/bodybuilding), il conçoit des programmes sur mesure qui vous emmènent 
                    plus loin que vous ne l'imaginez.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">Coach depuis 2005</span>
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">Diplômé FITSPRO</span>
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">Certifié EMS</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Tarah */}
            <AnimatedSection delay={200}>
              <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-primary/30 transition-all hover:shadow-xl hover:shadow-primary/10 group">
                <div className="aspect-square overflow-hidden bg-zinc-900 relative flex items-start justify-center">
                  <img 
                    src={TARAH_IMAGE} 
                    alt="Tarah Monnery - Personal Trainer" 
                    className="w-[65%] h-auto object-contain mt-[6%] group-hover:scale-105 transition-transform duration-500 relative z-10"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-900/80 z-20 pointer-events-none" />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(24,24,27,0.9)_100%)] z-20 pointer-events-none" />
                </div>
                <div className="p-8">
                  <span className="text-primary text-sm font-semibold">💪 Personal Trainer & Coach EMS</span>
                  <h3 className="text-2xl font-bold text-white mt-2 mb-4">Tarah Monnery</h3>
                  <p className="text-gray-400 mb-6">
                    Après une reconversion professionnelle, Tarah est coach sportive depuis plus de 5 ans. 
                    Spécialisée en musculation, personal training et cours collectifs, elle apporte son énergie et sa passion 
                    pour vous accompagner vers vos objectifs avec bienveillance et détermination.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">Diplômée STAPS</span>
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">Certifiée EMS</span>
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">+5 ans d'expérience</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <blockquote className="text-2xl md:text-4xl font-light text-white italic">
              "Prenez soin de votre corps, c'est le seul endroit où vous êtes obligé de vivre."
            </blockquote>
            <p className="text-primary mt-6 text-lg">— Jim Rohn</p>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t.about.meetCta}
            </h2>
            <p className="text-white/90 text-lg mb-8">
              {t.about.meetDescription}
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 hover:scale-105 transition-all">
                <Calendar className="w-5 h-5 mr-2" />
                {t.about.meetButton}
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Services Page

export default AboutPage;
